import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCopy,
  faCheck,
  faKey,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import moment from "moment";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

const ApiKeysTab = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);

  // Create form
  const [newKeyName, setNewKeyName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");

  // After creation – show key once
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    const res = await Utils._apiRequest({
      path: Enums.endpoints.apiKeys.LIST,
      type: "GET",
    });
    if (res?.data) {
      setApiKeys(res.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      toast.error("Vui lòng nhập tên API key.");
      return;
    }
    setCreating(true);

    const res = await Utils._apiRequest({
      path: Enums.endpoints.apiKeys.CREATE,
      type: "POST",
      payload: {
        name: newKeyName.trim(),
        expiresInDays: expiresInDays || null,
      },
    });

    if (res?.errors) {
      toast.error(res.errors.message || "Không thể tạo API key.");
    } else if (res?.data) {
      setCreatedKey(res.data);
      fetchKeys();
    }

    setCreating(false);
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`Bạn có chắc muốn thu hồi key "${name}"? Key sẽ không thể sử dụng lại.`)) {
      return;
    }

    const res = await Utils._apiRequest({
      path: Enums.endpoints.apiKeys.REVOKE,
      pathParams: { id },
      type: "PATCH",
    });

    if (res?.errors) {
      toast.error(res.errors.message || "Không thể thu hồi API key.");
    } else {
      toast.success("Đã thu hồi API key.");
      fetchKeys();
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Xoá vĩnh viễn key "${name}"? Hành động này không thể hoàn tác.`)) {
      return;
    }

    const res = await Utils._apiRequest({
      path: Enums.endpoints.apiKeys.DELETE,
      pathParams: { id },
      type: "DELETE",
    });

    if (res?.errors) {
      toast.error(res.errors.message || "Không thể xoá API key.");
    } else {
      toast.success("Đã xoá API key.");
      fetchKeys();
    }
  };

  const handleCopyKey = async (rawKey) => {
    try {
      await navigator.clipboard.writeText(rawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Không thể copy.");
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreatedKey(null);
    setNewKeyName("");
    setExpiresInDays("");
    setCopied(false);
  };

  const getStatusBadge = (key) => {
    if (key.status === "revoked") {
      return <Badge bg="danger">Đã thu hồi</Badge>;
    }
    if (key.expiresAt && new Date(key.expiresAt) < new Date()) {
      return <Badge bg="warning" text="dark">Hết hạn</Badge>;
    }
    return <Badge bg="success">Hoạt động</Badge>;
  };

  const isKeyUsable = (key) => {
    if (key.status === "revoked") return false;
    if (key.expiresAt && new Date(key.expiresAt) < new Date()) return false;
    return true;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <p className="mb-1 text-muted">
            Tạo API key để xác thực các request đến hệ thống thay cho tài khoản của bạn.
            Sử dụng header <code>Authorization: Bearer &lt;api_key&gt;</code>.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
          <FontAwesomeIcon icon={faPlus} className="me-1" /> Tạo API key
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" /> Đang tải...
        </div>
      ) : apiKeys.length === 0 ? (
        <Alert variant="info">Bạn chưa có API key nào. Hãy tạo key đầu tiên.</Alert>
      ) : (
        <Table responsive hover size="sm">
          <thead>
            <tr>
              <th>Tên</th>
              <th>Key</th>
              <th>Trạng thái</th>
              <th>Ngày tạo</th>
              <th>Hết hạn</th>
              <th>Lần dùng cuối</th>
              <th className="text-end">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((key) => (
              <tr key={key._id}>
                <td>
                  <FontAwesomeIcon icon={faKey} className="me-2 text-muted" />
                  {key.name}
                </td>
                <td>
                  <code>{key.keyPrefix}...{'*'.repeat(8)}</code>
                </td>
                <td>{getStatusBadge(key)}</td>
                <td>{moment(key.createdAt).format("DD/MM/YYYY")}</td>
                <td>
                  {key.expiresAt
                    ? moment(key.expiresAt).format("DD/MM/YYYY")
                    : <span className="text-muted">Không hết hạn</span>}
                </td>
                <td>
                  {key.lastUsedAt
                    ? moment(key.lastUsedAt).fromNow()
                    : <span className="text-muted">Chưa dùng</span>}
                </td>
                <td className="text-end">
                  {isKeyUsable(key) && (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="me-1"
                      title="Thu hồi"
                      onClick={() => handleRevoke(key._id, key.name)}
                    >
                      <FontAwesomeIcon icon={faBan} />
                    </Button>
                  )}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    title="Xoá"
                    onClick={() => handleDelete(key._id, key.name)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Create API Key Modal */}
      <Modal show={showCreateModal} onHide={closeCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {createdKey ? "API Key đã tạo thành công" : "Tạo API key mới"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createdKey ? (
            <div>
              <Alert variant="warning">
                <strong>Quan trọng:</strong> Đây là lần duy nhất bạn có thể thấy key này.
                Hãy lưu lại ngay!
              </Alert>
              <Form.Group className="mb-3">
                <Form.Label><strong>Tên</strong></Form.Label>
                <Form.Control readOnly value={createdKey.name} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label><strong>API Key</strong></Form.Label>
                <InputGroup>
                  <Form.Control
                    readOnly
                    value={createdKey.rawKey}
                    style={{ fontFamily: "monospace", fontSize: "0.85em" }}
                  />
                  <Button
                    variant={copied ? "success" : "secondary"}
                    onClick={() => handleCopyKey(createdKey.rawKey)}
                  >
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />{" "}
                    {copied ? "Đã copy" : "Copy"}
                  </Button>
                </InputGroup>
              </Form.Group>
              {createdKey.expiresAt && (
                <p className="text-muted mb-0">
                  Hết hạn: {moment(createdKey.expiresAt).format("DD/MM/YYYY HH:mm")}
                </p>
              )}
            </div>
          ) : (
            <Form onSubmit={handleCreate}>
              <Form.Group className="mb-3">
                <Form.Label>Tên API key</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="VD: Production Server, CI/CD Pipeline..."
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  maxLength={100}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Thời hạn (ngày)</Form.Label>
                <Form.Select
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                >
                  <option value="">Không hết hạn</option>
                  <option value="7">7 ngày</option>
                  <option value="30">30 ngày</option>
                  <option value="60">60 ngày</option>
                  <option value="90">90 ngày</option>
                  <option value="180">180 ngày</option>
                  <option value="365">365 ngày</option>
                </Form.Select>
              </Form.Group>
              <div className="text-end">
                <Button variant="secondary" className="me-2" onClick={closeCreateModal}>
                  Huỷ
                </Button>
                <Button variant="primary" type="submit" disabled={creating}>
                  {creating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" /> Đang tạo...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} className="me-1" /> Tạo key
                    </>
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
        {createdKey && (
          <Modal.Footer>
            <Button variant="primary" onClick={closeCreateModal}>
              Đã lưu, đóng
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </div>
  );
};

export default ApiKeysTab;
